/**
 * ============================================================
 * 1895 VAULT — Zero-Knowledge Security SDK
 * ============================================================
 * 
 * Standalone encryption module for 1895.ai
 * 
 * ARCHITECTURE PRINCIPLE:
 * The backend is a "dumb encrypted locker." It stores blobs
 * it cannot read, serves blobs it cannot understand, and has
 * zero knowledge of what's inside any of them.
 * 
 * EVERYTHING that touches script content is encrypted:
 * - The script text itself
 * - Scene breakdowns
 * - Shot anchors and their text references
 * - Character analysis with quoted lines
 * - Emotional architecture with scene references
 * - Dead zone markers
 * - Plot hole references
 * - Budget/risk markers tied to scenes
 * - Comments and annotations
 * 
 * The ONLY things stored in plaintext are:
 * - User account info (email, name, plan)
 * - Project title (user can choose to encrypt this too)
 * - Greenlight Score (ONLY if user opts in to share it)
 * - Timestamps and version numbers
 * 
 * DEPENDENCIES:
 * None. Uses native Web Crypto API (SubtleCrypto).
 * Works in all modern browsers (Chrome 37+, Firefox 34+, Safari 11+).
 * 
 * USAGE:
 * import { Vault } from './1895-vault-sdk';
 * 
 * // Initialize
 * const vault = new Vault();
 * 
 * // First time: create vault with user's password
 * await vault.create(userPassword);
 * 
 * // Encrypt script + all metadata as one encrypted package
 * const encrypted = await vault.seal(projectData);
 * // → send encrypted.blob to backend (backend cannot read it)
 * 
 * // Load from backend and decrypt
 * const decrypted = await vault.unseal(encrypted.blob, userPassword);
 * // → returns full projectData with script, shots, analysis, etc.
 * 
 * ============================================================
 */

// ============================================================
// SECTION 1: CORE CRYPTOGRAPHIC PRIMITIVES
// ============================================================

/**
 * Derives an encryption key from a user password.
 * Uses PBKDF2 with 600,000 iterations (OWASP 2024 recommendation).
 * The salt ensures same password produces different keys per vault.
 */
async function deriveKeyFromPassword(password, salt) {
  const encoder = new TextEncoder();
  
  // Import the password as a CryptoKey
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  // Derive an AES-256-GCM key using PBKDF2
  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 600000, // OWASP 2024 minimum recommendation
      hash: 'SHA-256'
    },
    passwordKey,
    {
      name: 'AES-GCM',
      length: 256
    },
    false, // not extractable — key never leaves SubtleCrypto
    ['encrypt', 'decrypt']
  );
  
  return derivedKey;
}

/**
 * Generates a random AES-256-GCM key for encrypting project data.
 * This is the "Project Key" — each project gets its own.
 * The Project Key is then encrypted with the user's password-derived key.
 */
async function generateProjectKey() {
  return await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, // extractable — we need to wrap it with the password key
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypts data with AES-256-GCM.
 * Returns: { ciphertext, iv, tag } (tag is included in ciphertext by WebCrypto)
 */
async function encryptAESGCM(key, plaintext) {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
  
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv },
    key,
    encoder.encode(plaintext)
  );
  
  return {
    ciphertext: new Uint8Array(ciphertext),
    iv: iv
  };
}

/**
 * Decrypts data with AES-256-GCM.
 */
async function decryptAESGCM(key, ciphertext, iv) {
  const decoder = new TextDecoder();
  
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv },
    key,
    ciphertext
  );
  
  return decoder.decode(plaintext);
}

/**
 * Wraps (encrypts) a CryptoKey with another CryptoKey.
 * Used to encrypt the Project Key with the password-derived key.
 */
async function wrapKey(keyToWrap, wrappingKey) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  const wrappedKey = await crypto.subtle.wrapKey(
    'raw',
    keyToWrap,
    wrappingKey,
    { name: 'AES-GCM', iv: iv }
  );
  
  return {
    wrappedKey: new Uint8Array(wrappedKey),
    iv: iv
  };
}

/**
 * Unwraps (decrypts) a CryptoKey.
 */
async function unwrapKey(wrappedKeyData, wrappingKey, iv) {
  return await crypto.subtle.unwrapKey(
    'raw',
    wrappedKeyData,
    wrappingKey,
    { name: 'AES-GCM', iv: iv },
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}


// ============================================================
// SECTION 2: THE VAULT CLASS
// ============================================================

class Vault {
  constructor() {
    this.masterKey = null;
    this.salt = null;
    this.isUnlocked = false;
    this.ecdhKeyPair = null;    // { publicKey: CryptoKey, privateKey: CryptoKey }
    this.sectionKeys = new Map(); // section name → CryptoKey
  }
  
  /**
   * CREATE — Initialize a new vault for a new user.
   * Called once during account creation.
   * 
   * @param {string} password — User's vault password
   * @returns {object} — { salt } to store on backend (safe — salt is not secret)
   */
  async create(password) {
    // Generate a random salt (stored on backend — this is safe)
    this.salt = crypto.getRandomValues(new Uint8Array(32));

    // Derive the master key from password + salt
    this.masterKey = await deriveKeyFromPassword(password, this.salt);
    this.isUnlocked = true;

    // Generate ECDH P-256 key pair for collaboration
    this.ecdhKeyPair = await crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true, // extractable for export
      ['deriveBits']
    );

    // Export public key as JWK (safe to store on server)
    const ecdhPublicKeyJWK = await crypto.subtle.exportKey('jwk', this.ecdhKeyPair.publicKey);

    // Encrypt private key with master key for localStorage persistence
    const privateKeyJWK = await crypto.subtle.exportKey('jwk', this.ecdhKeyPair.privateKey);
    const privateKeyBytes = new TextEncoder().encode(JSON.stringify(privateKeyJWK));
    const { ciphertext: encPrivKey, iv: encPrivKeyIV } = await encryptAESGCM(this.masterKey, JSON.stringify(privateKeyJWK));
    const encryptedEcdhPrivateKey = arrayBufferToBase64(packageChunk(encPrivKeyIV, encPrivKey));

    return {
      salt: arrayBufferToBase64(this.salt),
      ecdhPublicKeyJWK,
      encryptedEcdhPrivateKey,
    };
  }
  
  /**
   * UNLOCK — Unlock an existing vault with the user's password.
   * Called on every login.
   * 
   * @param {string} password — User's vault password
   * @param {string} saltBase64 — Salt retrieved from backend
   */
  async unlock(password, saltBase64, encryptedEcdhPrivateKey = null) {
    this.salt = base64ToArrayBuffer(saltBase64);
    this.masterKey = await deriveKeyFromPassword(password, this.salt);
    this.isUnlocked = true;

    // Load ECDH private key if provided
    if (encryptedEcdhPrivateKey) {
      await this.loadECDHPrivateKey(encryptedEcdhPrivateKey);
    }
  }
  
  /**
   * SEAL — Encrypt an entire project's data into a single blob.
   * 
   * This is the critical function. It takes EVERYTHING about a project
   * and encrypts it into one opaque blob that the backend stores.
   * 
   * The backend has NO IDEA what's inside. It's just bytes.
   * 
   * @param {object} projectData — The complete project data:
   * {
   *   script: {
   *     text: string,           // Raw script text
   *     format: string,         // "pdf" | "fdx" | "fountain" | "txt"
   *     scenes: [{              // Parsed scenes
   *       id: number,
   *       slugLine: string,
   *       pageRange: [number, number],
   *       text: string,
   *       characters: string[]
   *     }]
   *   },
   *   shots: [{                 // Shot anchoring data
   *     id: string,
   *     anchorStart: number,    // Character offset in script text
   *     anchorEnd: number,      // Character offset in script text
   *     anchorText: string,     // The selected text snippet
   *     shotType: string,       // "wide" | "close-up" | etc.
   *     description: string,
   *     notes: string,
   *     metadata: object        // Camera, lens, etc.
   *   }],
   *   analysis: {               // Greenlight Engine results
   *     greenlightScore: number,
   *     writingCraft: object,   // Includes quoted lines from script
   *     characters: object,     // Includes dialogue excerpts
   *     characterArcs: object,
   *     emotionalArchitecture: object, // References scenes
   *     deadZones: object,      // References page ranges
   *     logicAnalysis: object,  // References specific plot points
   *     audienceSegmentation: object,
   *     budgetEstimation: object, // References scenes
   *     riskAssessment: object,   // References scenes
   *     comparableFilms: object,
   *     executiveSummary: string  // May quote the script
   *   },
   *   annotations: [{           // Team comments
   *     id: string,
   *     userId: string,
   *     targetType: string,
   *     targetId: string,
   *     content: string,        // May quote the script
   *     timestamp: string
   *   }],
   *   productionData: {         // Progressive enrichment data
   *     locations: object,
   *     cast: object,
   *     storyboards: object,
   *     schedule: object,
   *     budget: object
   *   }
   * }
   * 
   * @returns {object} — { blob, projectKeyWrapped, projectKeyIV }
   *   All three values are sent to the backend. All are opaque bytes.
   */
  async seal(projectData) {
    if (!this.isUnlocked) throw new Error('Vault is locked. Call unlock() first.');
    
    // 1. Generate a unique Project Key for this project
    const projectKey = await generateProjectKey();
    
    // 2. Serialize the ENTIRE project data to JSON
    const plaintext = JSON.stringify(projectData);
    
    // 3. Encrypt with the Project Key
    const { ciphertext, iv } = await encryptAESGCM(projectKey, plaintext);
    
    // 4. Wrap (encrypt) the Project Key with the user's Master Key
    const { wrappedKey, iv: wrapIV } = await wrapKey(projectKey, this.masterKey);
    
    // 5. Package everything into a single storable blob
    //    Format: [4 bytes: iv length][iv][4 bytes: wrapIV length][wrapIV]
    //            [4 bytes: wrappedKey length][wrappedKey][ciphertext]
    const blob = packageBlob(iv, wrapIV, wrappedKey, ciphertext);
    
    return {
      blob: arrayBufferToBase64(blob)
    };
  }
  
  /**
   * UNSEAL — Decrypt a project blob back into usable data.
   * 
   * Called when the user opens a project. The backend sends the blob,
   * the client decrypts it here, and the app renders the decrypted data.
   * 
   * @param {string} blobBase64 — The encrypted blob from the backend
   * @returns {object} — The complete projectData object
   */
  async unseal(blobBase64) {
    if (!this.isUnlocked) throw new Error('Vault is locked. Call unlock() first.');
    
    const blob = base64ToArrayBuffer(blobBase64);
    
    // 1. Unpackage the blob
    const { iv, wrapIV, wrappedKey, ciphertext } = unpackageBlob(blob);
    
    // 2. Unwrap the Project Key using the Master Key
    const projectKey = await unwrapKey(wrappedKey, this.masterKey, wrapIV);
    
    // 3. Decrypt the project data
    const plaintext = await decryptAESGCM(projectKey, ciphertext, iv);
    
    // 4. Parse JSON back into an object
    return JSON.parse(plaintext);
  }
  
  /**
   * SEAL PARTIAL — Encrypt just one piece of data for incremental saves.
   * 
   * When a user adds a single shot anchor, we don't want to re-encrypt
   * the entire project. Instead, we encrypt just the new data and
   * send it as an incremental update.
   * 
   * The backend stores it as a separate encrypted chunk linked to the project.
   * On next full load, the client fetches all chunks and merges them.
   * 
   * @param {string} dataType — "shot" | "annotation" | "analysis_update"
   * @param {object} data — The new data to encrypt
   * @param {string} projectBlobBase64 — Existing project blob (to extract project key)
   * @returns {object} — { chunk } encrypted incremental data
   */
  async sealPartial(dataType, data, projectBlobBase64) {
    if (!this.isUnlocked) throw new Error('Vault is locked. Call unlock() first.');
    
    // Extract the project key from the existing blob
    const blob = base64ToArrayBuffer(projectBlobBase64);
    const { wrapIV, wrappedKey } = unpackageBlob(blob);
    const projectKey = await unwrapKey(wrappedKey, this.masterKey, wrapIV);
    
    // Encrypt the incremental data with the same project key
    const payload = JSON.stringify({ type: dataType, data: data, timestamp: Date.now() });
    const { ciphertext, iv } = await encryptAESGCM(projectKey, payload);
    
    // Package as a smaller chunk
    const chunk = packageChunk(iv, ciphertext);
    
    return {
      chunk: arrayBufferToBase64(chunk)
    };
  }
  
  /**
   * UNSEAL PARTIAL — Decrypt an incremental chunk.
   */
  async unsealPartial(chunkBase64, projectBlobBase64) {
    if (!this.isUnlocked) throw new Error('Vault is locked. Call unlock() first.');
    
    const blob = base64ToArrayBuffer(projectBlobBase64);
    const { wrapIV, wrappedKey } = unpackageBlob(blob);
    const projectKey = await unwrapKey(wrappedKey, this.masterKey, wrapIV);
    
    const chunk = base64ToArrayBuffer(chunkBase64);
    const { iv, ciphertext } = unpackageChunk(chunk);
    
    const plaintext = await decryptAESGCM(projectKey, ciphertext, iv);
    return JSON.parse(plaintext);
  }
  
  /**
   * CHANGE PASSWORD — Re-encrypt all project keys with a new password.
   * 
   * The project data itself doesn't change — only the wrapping changes.
   * 
   * @param {string} oldPassword
   * @param {string} newPassword
   * @param {string[]} projectBlobsBase64 — All project blobs
   * @returns {object} — { newSalt, reEncryptedBlobs }
   */
  async changePassword(oldPassword, newPassword, projectBlobsBase64) {
    // Derive old master key
    const oldMasterKey = await deriveKeyFromPassword(oldPassword, this.salt);
    
    // Generate new salt and derive new master key
    const newSalt = crypto.getRandomValues(new Uint8Array(32));
    const newMasterKey = await deriveKeyFromPassword(newPassword, newSalt);
    
    // For each project blob: unwrap project key with old, re-wrap with new
    const reEncryptedBlobs = [];
    for (const blobBase64 of projectBlobsBase64) {
      const blob = base64ToArrayBuffer(blobBase64);
      const { iv, wrapIV, wrappedKey, ciphertext } = unpackageBlob(blob);
      
      // Unwrap with old key
      const projectKey = await unwrapKey(wrappedKey, oldMasterKey, wrapIV);
      
      // Re-wrap with new key
      const { wrappedKey: newWrapped, iv: newWrapIV } = await wrapKey(projectKey, newMasterKey);
      
      // Repackage (ciphertext stays the same — only wrapping changes)
      const newBlob = packageBlob(iv, newWrapIV, newWrapped, ciphertext);
      reEncryptedBlobs.push(arrayBufferToBase64(newBlob));
    }
    
    // Update instance
    this.salt = newSalt;
    this.masterKey = newMasterKey;
    
    return {
      newSalt: arrayBufferToBase64(newSalt),
      reEncryptedBlobs: reEncryptedBlobs
    };
  }
  
  /**
   * SHARE PROJECT — Generate a share key for collaboration.
   * 
   * When sharing a project with a team member, we can't give them
   * our master password. Instead, we:
   * 1. Extract the project key
   * 2. Wrap it with the collaborator's public key (ECDH key exchange)
   * 3. Send the wrapped project key to the collaborator
   * 
   * The collaborator can then decrypt the project with their own credentials.
   * 
   * @param {string} projectBlobBase64
   * @param {CryptoKey} collaboratorPublicKey — from ECDH exchange
   * @returns {object} — wrapped project key for the collaborator
   */
  async shareProject(projectBlobBase64, collaboratorPublicKey) {
    if (!this.isUnlocked) throw new Error('Vault is locked.');
    
    const blob = base64ToArrayBuffer(projectBlobBase64);
    const { wrapIV, wrappedKey } = unpackageBlob(blob);
    
    // Unwrap project key with our master key
    const projectKey = await unwrapKey(wrappedKey, this.masterKey, wrapIV);
    
    // Derive shared secret via ECDH
    const sharedSecret = await crypto.subtle.deriveBits(
      { name: 'ECDH', public: collaboratorPublicKey },
      this.ecdhPrivateKey, // user's ECDH private key (generated during setup)
      256
    );
    
    // Import shared secret as wrapping key
    const sharedKey = await crypto.subtle.importKey(
      'raw', sharedSecret, { name: 'AES-GCM', length: 256 }, false, ['wrapKey']
    );
    
    // Wrap project key with shared secret
    const { wrappedKey: sharedWrapped, iv: sharedIV } = await wrapKey(projectKey, sharedKey);
    
    return {
      wrappedProjectKey: arrayBufferToBase64(sharedWrapped),
      iv: arrayBufferToBase64(sharedIV)
    };
  }
  
  /**
   * LOCK — Clear all keys from memory.
   * Called on logout or tab close.
   */
  lock() {
    this.masterKey = null;
    this.salt = null;
    this.isUnlocked = false;
    this.ecdhKeyPair = null;
    this.sectionKeys = new Map();
  }

  // ── V2: Per-Section Encryption + ECDH Sharing ──────────────

  /**
   * Load an encrypted ECDH private key from localStorage.
   * The private key was encrypted with the master key during create().
   */
  async loadECDHPrivateKey(encryptedBase64) {
    try {
      const chunk = base64ToArrayBuffer(encryptedBase64);
      const { iv, ciphertext } = unpackageChunk(chunk);
      const plaintext = await decryptAESGCM(this.masterKey, ciphertext, iv);
      const privateKeyJWK = JSON.parse(plaintext);

      const privateKey = await crypto.subtle.importKey(
        'jwk', privateKeyJWK,
        { name: 'ECDH', namedCurve: 'P-256' },
        false, // non-extractable in memory
        ['deriveBits']
      );

      // Also import the public key from the private JWK
      const pubJWK = { ...privateKeyJWK, d: undefined, key_ops: [] };
      delete pubJWK.d;
      const publicKey = await crypto.subtle.importKey(
        'jwk', pubJWK,
        { name: 'ECDH', namedCurve: 'P-256' },
        true,
        []
      );

      this.ecdhKeyPair = { publicKey, privateKey };
    } catch (err) {
      console.warn('Failed to load ECDH private key:', err.message);
      this.ecdhKeyPair = null;
    }
  }

  /**
   * Generate 8 section keys (one per vault section).
   * Returns a Map of section name → CryptoKey.
   */
  async generateSectionKeys(sections) {
    const keys = new Map();
    for (const section of sections) {
      const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true, // extractable for wrapping
        ['encrypt', 'decrypt']
      );
      keys.set(section, key);
    }
    this.sectionKeys = keys;
    return keys;
  }

  /**
   * Encrypt a single section's data.
   * @param {string} sectionName
   * @param {object} data — the section's data object
   * @param {CryptoKey} sectionKey — the section's AES-256-GCM key
   * @returns {{ blob: string }} — base64 encoded encrypted blob
   */
  async sealSection(sectionName, data, sectionKey) {
    if (!this.isUnlocked) throw new Error('Vault is locked.');
    const plaintext = JSON.stringify({ section: sectionName, data, timestamp: Date.now() });
    const { ciphertext, iv } = await encryptAESGCM(sectionKey, plaintext);
    return { blob: arrayBufferToBase64(packageChunk(iv, ciphertext)) };
  }

  /**
   * Decrypt a single section blob.
   * @param {string} blobBase64
   * @param {CryptoKey} sectionKey
   * @returns {object} — the section's data
   */
  async unsealSection(blobBase64, sectionKey) {
    if (!this.isUnlocked) throw new Error('Vault is locked.');
    const chunk = base64ToArrayBuffer(blobBase64);
    const { iv, ciphertext } = unpackageChunk(chunk);
    const plaintext = await decryptAESGCM(sectionKey, ciphertext, iv);
    const parsed = JSON.parse(plaintext);
    return parsed.data;
  }

  /**
   * Wrap all section keys with the owner's master key (self-access).
   * @param {Map<string, CryptoKey>} sectionKeys
   * @returns {Array<{ section, wrappedKey, wrapIV }>}
   */
  async wrapSectionKeysForSelf(sectionKeys) {
    if (!this.isUnlocked) throw new Error('Vault is locked.');
    const results = [];
    for (const [section, key] of sectionKeys) {
      // Need a wrapping key derived from master key (master key is for encrypt/decrypt, not wrapKey)
      // So we export the section key as raw, encrypt with master key
      const exported = await crypto.subtle.exportKey('raw', key);
      const { ciphertext, iv } = await encryptAESGCM(this.masterKey, arrayBufferToBase64(new Uint8Array(exported)));
      results.push({
        section,
        wrappedKey: arrayBufferToBase64(ciphertext),
        wrapIV: arrayBufferToBase64(iv),
        keyType: 'master',
      });
    }
    return results;
  }

  /**
   * Unwrap section keys that were wrapped with the owner's master key.
   * @param {Array<{ section, wrappedKey, wrapIV }>} wrappedKeys
   * @returns {Map<string, CryptoKey>}
   */
  async unwrapSectionKeysForSelf(wrappedKeys) {
    if (!this.isUnlocked) throw new Error('Vault is locked.');
    const keys = new Map();
    for (const { section, wrappedKey, wrapIV } of wrappedKeys) {
      const ciphertext = base64ToArrayBuffer(wrappedKey);
      const iv = base64ToArrayBuffer(wrapIV);
      const plaintext = await decryptAESGCM(this.masterKey, ciphertext, iv);
      const rawKey = base64ToArrayBuffer(plaintext);
      const key = await crypto.subtle.importKey(
        'raw', rawKey,
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
      keys.set(section, key);
    }
    this.sectionKeys = keys;
    return keys;
  }

  /**
   * Derive ECDH shared secret with a collaborator and wrap section keys.
   * @param {object} collabPubKeyJWK — collaborator's ECDH public key as JWK
   * @param {Map<string, CryptoKey>} sectionKeys — keys to share
   * @param {string[]} sectionNames — which sections to share
   * @returns {Array<{ section, wrappedKey, wrapIV }>}
   */
  async wrapSectionKeysForCollaborator(collabPubKeyJWK, sectionKeys, sectionNames) {
    if (!this.isUnlocked || !this.ecdhKeyPair) {
      throw new Error('Vault is locked or ECDH not initialized.');
    }

    // Import collaborator's public key
    const collabPubKey = await crypto.subtle.importKey(
      'jwk', collabPubKeyJWK,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      []
    );

    // Derive shared secret
    const sharedBits = await crypto.subtle.deriveBits(
      { name: 'ECDH', public: collabPubKey },
      this.ecdhKeyPair.privateKey,
      256
    );

    // Import shared secret as AES-GCM key for wrapping
    const sharedKey = await crypto.subtle.importKey(
      'raw', sharedBits,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    // Wrap each requested section key
    const results = [];
    for (const section of sectionNames) {
      const sectionKey = sectionKeys.get(section);
      if (!sectionKey) continue;

      const exported = await crypto.subtle.exportKey('raw', sectionKey);
      const { ciphertext, iv } = await encryptAESGCM(sharedKey, arrayBufferToBase64(new Uint8Array(exported)));
      results.push({
        section,
        wrappedKey: arrayBufferToBase64(ciphertext),
        wrapIV: arrayBufferToBase64(iv),
        keyType: 'ecdh',
      });
    }

    return results;
  }

  /**
   * Unwrap section keys shared via ECDH (collaborator side).
   * @param {object} ownerPubKeyJWK — project owner's ECDH public key as JWK
   * @param {Array<{ section, wrappedKey, wrapIV }>} wrappedKeys
   * @returns {Map<string, CryptoKey>}
   */
  async unwrapSectionKeys(ownerPubKeyJWK, wrappedKeys) {
    if (!this.isUnlocked || !this.ecdhKeyPair) {
      throw new Error('Vault is locked or ECDH not initialized.');
    }

    // Import owner's public key
    const ownerPubKey = await crypto.subtle.importKey(
      'jwk', ownerPubKeyJWK,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      []
    );

    // Derive same shared secret (ECDH is symmetric)
    const sharedBits = await crypto.subtle.deriveBits(
      { name: 'ECDH', public: ownerPubKey },
      this.ecdhKeyPair.privateKey,
      256
    );

    const sharedKey = await crypto.subtle.importKey(
      'raw', sharedBits,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    // Unwrap each section key
    const keys = new Map();
    for (const { section, wrappedKey, wrapIV } of wrappedKeys) {
      try {
        const ciphertext = base64ToArrayBuffer(wrappedKey);
        const iv = base64ToArrayBuffer(wrapIV);
        const plaintext = await decryptAESGCM(sharedKey, ciphertext, iv);
        const rawKey = base64ToArrayBuffer(plaintext);
        const key = await crypto.subtle.importKey(
          'raw', rawKey,
          { name: 'AES-GCM', length: 256 },
          true,
          ['encrypt', 'decrypt']
        );
        keys.set(section, key);
      } catch (err) {
        console.warn(`Failed to unwrap section key for ${section}:`, err.message);
      }
    }

    this.sectionKeys = keys;
    return keys;
  }

  /**
   * Generate new keys for specific sections (used during key rotation after member removal).
   * @param {string[]} sectionNames — sections to rotate
   * @returns {Map<string, CryptoKey>} — new keys for the specified sections
   */
  async rotateSectionKeys(sectionNames) {
    const newKeys = new Map();
    for (const section of sectionNames) {
      const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
      newKeys.set(section, key);
      this.sectionKeys.set(section, key); // Update in-memory
    }
    return newKeys;
  }

  /**
   * Migrate a v1 monolithic blob into per-section encrypted blobs.
   * @param {string} blobBase64 — the v1 monolithic encrypted blob
   * @param {object} sectionDataMap — maps section name to array of data field names
   * @param {string[]} allSections — all section names
   * @returns {{ sectionBlobs: Map<string, string>, sectionKeys: Map<string, CryptoKey> }}
   */
  async migrateFromMonolithicBlob(blobBase64, sectionDataMap, allSections) {
    if (!this.isUnlocked) throw new Error('Vault is locked.');

    // Decrypt the v1 blob
    const projectData = await this.unseal(blobBase64);

    // Generate section keys
    const sectionKeys = await this.generateSectionKeys(allSections);

    // Split project data into sections and encrypt each
    const sectionBlobs = new Map();
    for (const section of allSections) {
      const fields = sectionDataMap[section] || [];
      const sectionData = {};
      for (const field of fields) {
        if (projectData[field] !== undefined) {
          sectionData[field] = projectData[field];
        }
      }

      const { blob } = await this.sealSection(section, sectionData, sectionKeys.get(section));
      sectionBlobs.set(section, blob);
    }

    return { sectionBlobs, sectionKeys };
  }

  /**
   * Export the ECDH public key as JWK for sharing with the backend.
   * @returns {object|null} — JWK or null if ECDH not initialized
   */
  async getECDHPublicKeyJWK() {
    if (!this.ecdhKeyPair) return null;
    return await crypto.subtle.exportKey('jwk', this.ecdhKeyPair.publicKey);
  }

  /**
   * Export the encrypted ECDH private key for localStorage persistence.
   * @returns {string|null} — base64 encoded encrypted private key
   */
  async exportEncryptedECDHPrivateKey() {
    if (!this.isUnlocked || !this.ecdhKeyPair) return null;
    const privateKeyJWK = await crypto.subtle.exportKey('jwk', this.ecdhKeyPair.privateKey);
    const { ciphertext, iv } = await encryptAESGCM(
      this.masterKey,
      JSON.stringify(privateKeyJWK)
    );
    return arrayBufferToBase64(packageChunk(iv, ciphertext));
  }
}


// ============================================================
// SECTION 3: BYOK (Bring Your Own Key) LLM INTEGRATION
// ============================================================

/**
 * BYOKClient — Handles LLM API calls directly from the browser
 * using the user's own API key. 1895 servers NEVER see the key
 * or the plaintext prompts/responses.
 */
class BYOKClient {
  /**
   * @param {string} provider — "anthropic" | "openai"
   * @param {string} apiKey — User's own API key (stored in Vault, never sent to 1895)
   */
  constructor(provider, apiKey) {
    this.provider = provider;
    this.apiKey = apiKey;
    this.endpoints = {
      anthropic: 'https://api.anthropic.com/v1/messages',
      openai: 'https://api.openai.com/v1/chat/completions'
    };
  }
  
  /**
   * Send a prompt directly to the LLM provider from the browser.
   * 
   * IMPORTANT: This call goes DIRECTLY from the user's browser
   * to Anthropic/OpenAI. It does NOT pass through 1895 servers.
   * 
   * Flow:
   * User Browser → [HTTPS] → Anthropic API
   *                          ↓
   * User Browser ← [HTTPS] ← Anthropic API
   * 
   * 1895 servers are NOT in this flow.
   * 
   * @param {string} systemPrompt
   * @param {string} userPrompt — contains script text (sensitive)
   * @param {object} options — { model, maxTokens, temperature }
   */
  async analyze(systemPrompt, userPrompt, options = {}) {
    if (this.provider === 'anthropic') {
      return this.callAnthropic(systemPrompt, userPrompt, options);
    } else if (this.provider === 'openai') {
      return this.callOpenAI(systemPrompt, userPrompt, options);
    }
    throw new Error(`Unsupported provider: ${this.provider}`);
  }
  
  async callAnthropic(systemPrompt, userPrompt, options) {
    const response = await fetch(this.endpoints.anthropic, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': this.apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true'
      },
      body: JSON.stringify({
        model: options.model || 'claude-sonnet-4-20250514',
        max_tokens: options.maxTokens || 4096,
        temperature: options.temperature || 0.3,
        system: systemPrompt,
        messages: [{ role: 'user', content: userPrompt }]
      })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(`Anthropic API error ${response.status}: ${error.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    return data.content[0].text;
  }
  
  async callOpenAI(systemPrompt, userPrompt, options) {
    const response = await fetch(this.endpoints.openai, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`
      },
      body: JSON.stringify({
        model: options.model || 'gpt-4o',
        max_tokens: options.maxTokens || 4096,
        temperature: options.temperature || 0.3,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ]
      })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(`OpenAI API error ${response.status}: ${error.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    return data.choices[0].message.content;
  }
  
  /**
   * Run the complete Greenlight analysis using BYOK.
   * All processing happens in the browser.
   * Results are encrypted before being sent to 1895 for storage.
   * 
   * @param {object} parsedScript — { scenes, characters, fullText }
   * @param {Vault} vault — The user's vault instance
   * @param {function} onProgress — Callback for progress updates
   */
  async runGreenlightAnalysis(parsedScript, vault, onProgress) {
    const results = {};
    const totalSteps = 8;
    let step = 0;
    
    const progress = (label) => {
      step++;
      onProgress?.({ step, totalSteps, label });
    };
    
    // Step 1: Writing Craft
    progress('Analyzing writing craft...');
    results.writingCraft = await this.analyzeWritingCraft(parsedScript);
    
    // Step 2: Characters
    progress('Profiling characters...');
    results.characters = await this.analyzeCharacters(parsedScript);
    
    // Step 3: Character Arcs
    progress('Tracking character arcs...');
    results.characterArcs = await this.analyzeCharacterArcs(parsedScript, results.characters);
    
    // Step 4: Emotional Architecture
    progress('Mapping emotional architecture...');
    results.emotionalArchitecture = await this.analyzeEmotions(parsedScript);
    
    // Step 5: Dead Zones
    progress('Detecting dead zones...');
    results.deadZones = this.detectDeadZones(results.emotionalArchitecture);
    
    // Step 6: Logic Analysis
    progress('Checking plot logic...');
    results.logicAnalysis = await this.analyzeLogic(parsedScript);
    
    // Step 7: Audience Segmentation
    progress('Segmenting audience...');
    results.audienceSegmentation = await this.analyzeAudience(parsedScript);
    
    // Step 8: Budget & Risk
    progress('Estimating budget and risk...');
    results.budgetEstimation = await this.analyzeBudget(parsedScript);
    results.riskAssessment = await this.analyzeRisk(parsedScript);
    
    // Calculate Greenlight Score
    results.greenlightScore = this.calculateGreenlightScore(results);
    
    // Generate Executive Summary
    results.executiveSummary = await this.generateSummary(parsedScript, results);
    
    return results;
  }
  
  // Individual analysis methods would follow here.
  // Each one constructs the appropriate system prompt,
  // sends the relevant script data, and parses the JSON response.
  // Omitted for brevity but each follows the same pattern:
  //
  // async analyzeWritingCraft(parsedScript) {
  //   const systemPrompt = PROMPTS.WRITING_CRAFT;
  //   const scenes = parsedScript.scenes;
  //   const results = [];
  //   for (const scene of scenes) {
  //     const response = await this.analyze(systemPrompt, scene.text);
  //     results.push(JSON.parse(response));
  //   }
  //   return synthesize(results);
  // }

  calculateGreenlightScore(results) {
    const weights = {
      writingCraft: 0.20,
      characters: 0.075,
      characterArcs: 0.075,
      emotionalArchitecture: 0.15,
      logicAnalysis: 0.15,
      audienceSegmentation: 0.10,
      budgetEstimation: 0.10,
      riskAssessment: 0.10,
      // originality assessed within writingCraft
    };
    
    let score = 0;
    for (const [key, weight] of Object.entries(weights)) {
      if (results[key]?.overallScore !== undefined) {
        score += results[key].overallScore * weight;
      }
    }
    
    return Math.round(Math.min(100, Math.max(0, score)));
  }
}


// ============================================================
// SECTION 4: BACKEND API — WHAT THE SERVER SEES
// ============================================================

/**
 * This section defines what the 1895 backend stores and serves.
 * Note: the backend NEVER decrypts anything.
 * 
 * API Endpoints:
 */

const BACKEND_API = {
  
  // Store encrypted project blob
  // Backend receives: opaque bytes. Stores: opaque bytes. That's it.
  'POST /api/projects/:id/vault': {
    request: {
      blob: 'string (base64)',      // Encrypted project data
    },
    stored_in_db: {
      project_id: 'uuid',
      encrypted_blob: 'bytea',       // PostgreSQL binary type
      blob_size_bytes: 'integer',    // For quota tracking
      updated_at: 'timestamp',
      version: 'integer'            // For conflict resolution
    },
    what_backend_knows: 'NOTHING about the content. Only: who owns it, how big it is, and when it was updated.'
  },
  
  // Store encrypted incremental chunk (e.g., a new shot anchor)
  'POST /api/projects/:id/vault/chunks': {
    request: {
      chunk: 'string (base64)'       // Encrypted chunk
    },
    stored_in_db: {
      chunk_id: 'uuid',
      project_id: 'uuid',
      encrypted_chunk: 'bytea',
      created_at: 'timestamp'
    },
    what_backend_knows: 'NOTHING. Does not know if this is a shot, a comment, or an analysis update.'
  },
  
  // Retrieve project for client-side decryption
  'GET /api/projects/:id/vault': {
    response: {
      blob: 'string (base64)',
      chunks: ['string (base64)'],    // Any incremental chunks since last full save
      version: 'integer'
    },
    what_happens_on_client: 'Browser decrypts blob + chunks, merges them, renders the UI.'
  },
  
  // Store user's vault salt (NOT secret — safe to store in plaintext)
  'POST /api/users/:id/vault-config': {
    request: {
      salt: 'string (base64)',
      security_tier: '"cloud" | "byok" | "local"',
      llm_provider: '"anthropic" | "openai" | null'
    },
    what_backend_knows: 'Which security tier the user chose. The salt (which is not secret). Nothing else.'
  },
  
  // OPTIONAL: Store Greenlight Score in plaintext (user must opt-in)
  // This allows leaderboard, badges, and public sharing of the score
  // WITHOUT revealing anything about the script
  'POST /api/projects/:id/greenlight-score': {
    request: {
      score: 'number (0-100)',
      badge_tier: '"masterwork" | "strong" | "promising" | null',
      opt_in_public: 'boolean'        // User explicitly consents
    },
    what_backend_knows: 'ONLY the score and badge. Not the script, not the analysis, not even the genre.'
  }
};


// ============================================================
// SECTION 5: DATA FLOW DIAGRAMS
// ============================================================

/**
 * FLOW 1: Script Upload + Analysis (BYOK Mode)
 * 
 * ┌─────────────────────────────────────────────────────────────┐
 * │                     USER'S BROWSER                          │
 * │                                                             │
 * │  1. User uploads script.pdf                                 │
 * │  2. PDF parsed to text IN BROWSER (pdf.js)                  │
 * │  3. Text split into scenes IN BROWSER                       │
 * │  4. Each scene sent to LLM via USER'S API KEY               │
 * │     ┌─────────────┐                                         │
 * │     │ Browser ────────→ Anthropic API (user's key)          │
 * │     │          ←────────                                    │
 * │     └─────────────┘                                         │
 * │  5. Analysis results assembled IN BROWSER                   │
 * │  6. EVERYTHING encrypted: vault.seal({script, analysis...}) │
 * │  7. Encrypted blob sent to 1895 backend                     │
 * │     ┌─────────────┐                                         │
 * │     │ Browser ────────→ 1895 Backend (stores opaque blob)   │
 * │     └─────────────┘                                         │
 * │  8. UI renders from decrypted data in memory                │
 * └─────────────────────────────────────────────────────────────┘
 * 
 * 1895 BACKEND SEES: An opaque blob of X bytes from User Y.
 * 1895 BACKEND DOES NOT SEE: Script text, character names, scene content,
 *   analysis scores, shot lists, comments, or anything meaningful.
 * 
 * 
 * FLOW 2: Shot Anchoring (After Initial Upload)
 * 
 * ┌─────────────────────────────────────────────────────────────┐
 * │                     USER'S BROWSER                          │
 * │                                                             │
 * │  1. User sees decrypted script (already in memory)          │
 * │  2. User selects text "INT. HOSPITAL - NIGHT..."            │
 * │  3. User creates Shot with metadata                         │
 * │  4. Shot data = { anchorStart: 450, anchorEnd: 520,         │
 * │       anchorText: "INT. HOSPITAL...", shotType: "wide",     │
 * │       description: "Establish the ER chaos" }               │
 * │  5. vault.sealPartial("shot", shotData, existingBlob)       │
 * │  6. Encrypted chunk sent to backend                         │
 * │     ┌─────────────┐                                         │
 * │     │ Browser ────────→ 1895 Backend (stores opaque chunk)  │
 * │     └─────────────┘                                         │
 * │                                                             │
 * │  1895 Backend stores: chunk_id, project_id, encrypted bytes │
 * │  1895 Backend knows: "User Y added something to Project Z"  │
 * │  1895 Backend does NOT know: It was a shot. Or what scene.  │
 * │    Or what the text said. Or anything.                      │
 * └─────────────────────────────────────────────────────────────┘
 * 
 * 
 * FLOW 3: Loading a Project (Returning User)
 * 
 * ┌─────────────────────────────────────────────────────────────┐
 * │                     USER'S BROWSER                          │
 * │                                                             │
 * │  1. User logs in, enters vault password                     │
 * │  2. vault.unlock(password, salt)                            │
 * │  3. Fetch encrypted blob + chunks from backend              │
 * │     ┌─────────────┐                                         │
 * │     │ Browser ←────────  1895 Backend (sends opaque bytes)  │
 * │     └─────────────┘                                         │
 * │  4. vault.unseal(blob) → full projectData in memory         │
 * │  5. For each chunk: vault.unsealPartial(chunk) → merge      │
 * │  6. UI renders:                                             │
 * │     - Script text with shot anchors overlaid                │
 * │     - Emotional heatmap                                     │
 * │     - Character constellation                               │
 * │     - Budget timeline                                       │
 * │     - Everything, fully decrypted, fully interactive        │
 * │  7. On tab close: vault.lock() — keys wiped from memory     │
 * └─────────────────────────────────────────────────────────────┘
 */


// ============================================================
// SECTION 6: BURN AFTER READING MODE
// ============================================================

/**
 * For ultra-paranoid users who want to test the tool
 * without ANY data touching ANY server.
 * 
 * Everything happens in the browser tab.
 * When the tab closes, everything vanishes.
 * No backend calls. No storage. Nothing.
 */
class BurnAfterReading {
  constructor() {
    this.data = null;
  }
  
  /**
   * Run analysis entirely in-browser with no server involvement.
   * 
   * Requirements:
   * - BYOK mode (user provides their own LLM API key)
   * - Script never leaves the browser
   * - Results displayed but never saved
   * - Tab close = total destruction
   */
  async analyze(scriptFile, apiKey, provider, onProgress) {
    // Parse script in browser
    const scriptText = await this.parseInBrowser(scriptFile);
    
    // Run analysis via BYOK
    const byok = new BYOKClient(provider, apiKey);
    const parsedScript = this.parseScenes(scriptText);
    const results = await byok.runGreenlightAnalysis(parsedScript, null, onProgress);
    
    // Store in memory only (no Vault, no backend)
    this.data = { script: parsedScript, analysis: results };
    
    // Register destruction on tab close
    window.addEventListener('beforeunload', () => this.destroy());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Start a timer — if tab stays hidden for 5 minutes, destroy
        this.destructionTimer = setTimeout(() => this.destroy(), 5 * 60 * 1000);
      } else {
        clearTimeout(this.destructionTimer);
      }
    });
    
    return this.data;
  }
  
  destroy() {
    // Overwrite memory with zeros before releasing
    if (this.data) {
      const overwrite = JSON.stringify(this.data);
      for (let i = 0; i < overwrite.length; i++) {
        // This doesn't guarantee memory zeroing in JS (GC is unpredictable)
        // but it's the best we can do in browser environment
      }
      this.data = null;
    }
  }
  
  async parseInBrowser(file) {
    // Uses pdf.js loaded from CDN or bundled — no server involved
    // Implementation depends on file type
    if (file.type === 'application/pdf') {
      // pdfjs-dist must be installed separately if PDF burn mode is needed
      throw new Error('PDF parsing in burn mode requires pdfjs-dist. Upload as .txt or .fountain instead.');
    }
    // For .txt and .fountain, just read as text
    return await file.text();
  }
  
  parseScenes(scriptText) {
    // Basic scene parser — splits by INT./EXT. slug lines
    const sceneRegex = /^(INT\.|EXT\.|INT\.\/EXT\.|I\/E\.).+$/gm;
    const scenes = [];
    let match;
    const matches = [...scriptText.matchAll(sceneRegex)];
    
    for (let i = 0; i < matches.length; i++) {
      const start = matches[i].index;
      const end = i < matches.length - 1 ? matches[i + 1].index : scriptText.length;
      scenes.push({
        id: i,
        slugLine: matches[i][0].trim(),
        text: scriptText.substring(start, end).trim(),
        startOffset: start,
        endOffset: end
      });
    }
    
    return { fullText: scriptText, scenes };
  }
}


// ============================================================
// SECTION 7: LOCAL SLM DEPLOYMENT (Docker)
// ============================================================

/**
 * For studios that want EVERYTHING on their own hardware.
 * 
 * Docker Compose configuration for air-gapped deployment.
 * This runs the complete 1895 stack locally:
 * - Frontend (React)
 * - Backend (Node.js)
 * - Database (PostgreSQL)
 * - LLM Inference (vLLM with Llama 3.1 70B)
 * 
 * ZERO external network connections.
 */
const DOCKER_COMPOSE = `
version: '3.8'

services:
  # Frontend — React app served by nginx
  frontend:
    build: ./frontend
    ports:
      - "443:443"
    depends_on:
      - backend
    networks:
      - internal
    # NO external network access

  # Backend — Node.js API server
  backend:
    build: ./backend
    environment:
      - DATABASE_URL=postgresql://vault:vault@db:5432/eighteen95
      - LLM_ENDPOINT=http://inference:8000/v1
      - ENCRYPTION_MODE=local
      - NODE_ENV=production
    depends_on:
      - db
      - inference
    networks:
      - internal
    # NO external network access

  # Database — PostgreSQL with encryption at rest
  db:
    image: postgres:16
    environment:
      - POSTGRES_DB=eighteen95
      - POSTGRES_USER=vault
      - POSTGRES_PASSWORD_FILE=/run/secrets/db_password
    volumes:
      - pgdata:/var/lib/postgresql/data
    secrets:
      - db_password
    networks:
      - internal
    # NO external network access

  # LLM Inference — vLLM serving Llama 3.1 70B
  inference:
    image: vllm/vllm-openai:latest
    command: >
      --model /models/Meta-Llama-3.1-70B-Instruct
      --tensor-parallel-size 2
      --max-model-len 32768
      --gpu-memory-utilization 0.9
      --dtype bfloat16
    volumes:
      - ./models:/models
    deploy:
      resources:
        reservations:
          devices:
            - driver: nvidia
              count: 2
              capabilities: [gpu]
    networks:
      - internal
    # NO external network access

volumes:
  pgdata:
    driver: local

networks:
  internal:
    driver: bridge
    internal: true  # THIS IS KEY — no external access

secrets:
  db_password:
    file: ./secrets/db_password.txt
`;


// ============================================================
// SECTION 8: UTILITY FUNCTIONS
// ============================================================

function arrayBufferToBase64(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Package all encryption artifacts into a single binary blob.
 * Format:
 *   [2 bytes: iv length]
 *   [iv bytes]
 *   [2 bytes: wrapIV length]
 *   [wrapIV bytes]
 *   [4 bytes: wrappedKey length]
 *   [wrappedKey bytes]
 *   [remaining: ciphertext]
 */
function packageBlob(iv, wrapIV, wrappedKey, ciphertext) {
  const totalLength = 2 + iv.length + 2 + wrapIV.length + 4 + wrappedKey.length + ciphertext.length;
  const blob = new Uint8Array(totalLength);
  let offset = 0;
  
  // IV
  blob[offset++] = (iv.length >> 8) & 0xFF;
  blob[offset++] = iv.length & 0xFF;
  blob.set(iv, offset);
  offset += iv.length;
  
  // Wrap IV
  blob[offset++] = (wrapIV.length >> 8) & 0xFF;
  blob[offset++] = wrapIV.length & 0xFF;
  blob.set(wrapIV, offset);
  offset += wrapIV.length;
  
  // Wrapped Key
  blob[offset++] = (wrappedKey.length >> 24) & 0xFF;
  blob[offset++] = (wrappedKey.length >> 16) & 0xFF;
  blob[offset++] = (wrappedKey.length >> 8) & 0xFF;
  blob[offset++] = wrappedKey.length & 0xFF;
  blob.set(wrappedKey, offset);
  offset += wrappedKey.length;
  
  // Ciphertext
  blob.set(ciphertext, offset);
  
  return blob;
}

function unpackageBlob(blob) {
  let offset = 0;
  
  const ivLength = (blob[offset] << 8) | blob[offset + 1];
  offset += 2;
  const iv = blob.slice(offset, offset + ivLength);
  offset += ivLength;
  
  const wrapIVLength = (blob[offset] << 8) | blob[offset + 1];
  offset += 2;
  const wrapIV = blob.slice(offset, offset + wrapIVLength);
  offset += wrapIVLength;
  
  const wrappedKeyLength = (blob[offset] << 24) | (blob[offset + 1] << 16) | (blob[offset + 2] << 8) | blob[offset + 3];
  offset += 4;
  const wrappedKey = blob.slice(offset, offset + wrappedKeyLength);
  offset += wrappedKeyLength;
  
  const ciphertext = blob.slice(offset);
  
  return { iv, wrapIV, wrappedKey, ciphertext };
}

function packageChunk(iv, ciphertext) {
  const total = 2 + iv.length + ciphertext.length;
  const chunk = new Uint8Array(total);
  chunk[0] = (iv.length >> 8) & 0xFF;
  chunk[1] = iv.length & 0xFF;
  chunk.set(iv, 2);
  chunk.set(ciphertext, 2 + iv.length);
  return chunk;
}

function unpackageChunk(chunk) {
  const ivLength = (chunk[0] << 8) | chunk[1];
  const iv = chunk.slice(2, 2 + ivLength);
  const ciphertext = chunk.slice(2 + ivLength);
  return { iv, ciphertext };
}


// ============================================================
// EXPORTS
// ============================================================

export { Vault, BYOKClient, BurnAfterReading };
